import { MenuContext } from './context.js';
import { MenuItem } from './MenuItem.js';
import { MenuItemSeparator } from './MenuItemSeparator.js';
import { MenuPanel } from './MenuPanel.js';
import { MenuReference } from './MenuReference.js';
import { type MenuProps } from './types.js';
import { useIsomorphicLayoutEffect } from '@contra/react-hooks/useIsomorphicLayoutEffect';
import { useMemo, useRef } from 'react';
import { usePopper } from 'react-popper';

const fallback = {};

const Menu = ({
  children,
  darkMode = false,
  hasBorder = false,
  isOpen,
  offset,
  placement,
  strategy = 'fixed',
  toggleMenu = () => {},
}: MenuProps) => {
  const referenceElement = useRef<HTMLDivElement>(null);
  const popperElement = useRef<HTMLDivElement>(null);
  const { attributes, styles, update } = usePopper(
    referenceElement.current,
    popperElement.current,
    {
      modifiers: [
        {
          name: 'offset',
          options: {
            offset,
          },
        },
        {
          enabled: true,
          name: 'hide',
        },
      ],
      placement: placement ?? 'bottom-start',
      strategy,
    },
  );
  const popperStyles = styles || fallback;
  const popperAttributes = attributes || fallback;

  useIsomorphicLayoutEffect(() => {
    if (isOpen && update) void update();
  }, [isOpen, update]);

  const context = useMemo(() => {
    return {
      darkMode,
      hasBorder,
      isOpen,
      popperAttributes,
      popperElement,
      popperStyles,
      referenceElement,
      toggleMenu,
    };
  }, [
    darkMode,
    hasBorder,
    isOpen,
    popperAttributes,
    popperElement,
    popperStyles,
    referenceElement,
    toggleMenu,
  ]);

  return (
    <MenuContext.Provider value={context}>{children}</MenuContext.Provider>
  );
};

Menu.Reference = MenuReference;
Menu.Panel = MenuPanel;
Menu.Item = MenuItem;
Menu.ItemSeparator = MenuItemSeparator;

export { Menu };
